import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const CodeBlock = makeShortcode("CodeBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "aepimage"
    }}>{`AEPImage`}</h1>
    <p>{`The AEPImage class is a fundamental UI component used to display images for content cards.`}</p>
    <p>{`Images can be displayed from various sources (URL, bundle, or SF Symbols) and their appearance can be customized.`}</p>
    <p>{`AEPImage conforms to `}<inlineCode parentName="p">{`ObservableObject`}</inlineCode>{`, allowing it to be used reactively in SwiftUI views.`}</p>
    <h2 {...{
      "id": "image-sources"
    }}>{`Image Sources`}</h2>
    <p>{`The AEPImage class supports multiple sources for displaying images, with built-in support for both light and dark mode variants.`}</p>
    <p>{`Image sources are prioritized in the following order:`}</p>
    <ul>
      <li parentName="ul">{`URL images`}</li>
      <li parentName="ul">{`Bundled resource images`}</li>
      <li parentName="ul">{`SF Symbol icons`}</li>
    </ul>
    <h2 {...{
      "id": "public-properties"
    }}>{`Public properties`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default Value`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`contentMode`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.apple.com/documentation/uikit/uiview/1622619-contentmode"
            }}>{`ContentMode`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The content mode of the image.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.apple.com/documentation/swiftui/contentmode/fit"
            }}>{`ContentMode.fit`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`modifier`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`AEPViewModifier`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A custom view modifier that can be applied to the text view for additional styling.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`icon`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The name of the SF Symbol icon used in the image.`}<br />{` This applies only to images sourced from icons.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`iconFont`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.apple.com/documentation/swiftui/font"
            }}>{`Font`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The font of the SF Symbol icon used in the image.`}<br />{`This applies only to images sourced from icons.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`iconColor`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.apple.com/documentation/swiftui/color"
            }}>{`Color`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The color of the SF Symbol icon used in the image.`}<br />{` This applies only to images sourced from icons.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.apple.com/documentation/swiftui/color/primary"
            }}>{`primary`}</a></td>
        </tr>
      </tbody>
    </table>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`All properties are marked with `}<a parentName="p" {...{
        "href": "https://developer.apple.com/documentation/combine/published"
      }}>{`@Published`}</a>{`. Any changes will trigger updates to the UI.`}</p>
    <h2 {...{
      "id": "customization"
    }}>{`Customization`}</h2>
    <p>{`You can customize AEPImage properties when working with a template that includes an image. Here's an example:`}</p>
    <CodeBlock slots="heading, code" repeat="1" languages="Swift" mdxType="CodeBlock" />
    <h4 {...{
      "id": "swift"
    }}>{`Swift`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`class MyCustomizer: ContentCardCustomizing {
    func customize(template: SmallImageTemplate) {
        // Customize the image element
        template.image.contentMode = .fill
        template.image.modifier = AEPViewModifier(MyImageModifier())

        // Customize the dismiss button
        // dismiss buttons are images sourced from SF Symbols icons
        template.dismissButton?.image.iconName = "xmark" // SF Symbol name
        template.dismissButton?.image.iconColor = .primary
        template.dismissButton?.image.iconFont = .system(size: 10)        
    }
    
    struct MyImageModifier: ViewModifier {
        func body(content: Content) -> some View {
            content
                .clipShape(Circle())
                .overlay(Circle().stroke(Color.red, lineWidth: 2))
        }
    }
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      